import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import Select from "react-select";
import { getReportData } from "./ReportsAPI";

// colours for each comms/status type
const colours = {
  sms: "#880",
  voice: "#088",
  email: "#808",
  delivered: "#46be8a",
  sent: "#926dde",
  failed: "#62a8ea",
  expired: "#f96868",
  pending: "#f96868",
  cancelled: "#f96868",
  unknown: "#f96868",
};

// labels for each comms/status type
const labels = {
  sms: (
    <span title="SMS">
      <i className="fa fa-comment" /> SMS
    </span>
  ),
  email: (
    <span title="e-mail">
      <i className="fa fa-envelope" /> Email
    </span>
  ),
  voice: (
    <span title="Voice">
      <i className="fa fa-microphone" /> Voice
    </span>
  ),
  delivered: (
    <span title="Delivered">
      <i className="fa fa-check" /> Delivered
    </span>
  ),
  sent: (
    <span title="Sent">
      <i className="fa fa-share" /> Sent
    </span>
  ),
  expired: (
    <span title="Expired">
      <i className="fa fa-clock-o" /> Expired
    </span>
  ),
  pending: (
    <span title="Pending">
      <i className="fa fa-clock-o" /> Pending
    </span>
  ),
  cancelled: (
    <span title="Cancelled">
      <i className="fa fa-clock-o" /> Cancelled
    </span>
  ),
  unknown: (
    <span title="Unknown">
      <i className="fa fa-clock-o" /> Unknown
    </span>
  ),
  failed: (
    <span title="Failed">
      <i className="fa fa-times" /> Failed
    </span>
  ),
};

// Tooltip that show only non-zero values
function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          background: "white",
          border: "solid 1px #ddd",
          borderRadius: "4px",
          padding: 5,
        }}
      >
        <div>{label}</div>
        {payload
          .filter((data) => data.value > 0)
          .map((data) => (
            <div style={{ color: data.fill }} key={data.dataKey}>
              {data.name}: {data.value}
            </div>
          ))}
        total: {payload.reduce((p, c) => p + c.value, 0)}
      </div>
    );
  }

  return null;
}

// renders bars with colours
function BarWithColour(props) {
  const { fill, x, y, width, height } = props;
  return (
    height > 1 && (
      <rect
        x={x}
        y={y}
        width={width}
        height={height - 1}
        stroke="none"
        fill={fill}
      />
    )
  );
}

const stackOptions = [
  { value: "messageStatus", label: "Status" },
  { value: "comms", label: "Channel" },
];

export default function Graph({ filters, loading, setLoading }) {
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [stackBy, setStackBy] = useState(stackOptions[1]);

  let total = 0;
  data.forEach((d) => {
    Object.values(d.email).forEach((v) => {
      if (typeof v === "number") {
        total += v;
      }
    });
    Object.values(d.sms).forEach((v) => {
      if (typeof v === "number") {
        total += v;
      }
    });
    Object.values(d.voice).forEach((v) => {
      if (typeof v === "number") {
        total += v;
      }
    });
  });

  // renders legends box on the left hand side of the graph
  const renderLegend = () => (
    <div>
      <div>Total: {total}</div>
      <br />
      <span>Stack by</span>
      <Select options={stackOptions} value={stackBy} onChange={setStackBy} />
      <br />
      <ul className="margin-top-20 list-group">
        {filters?.[stackBy.value].map((item) => (
          <li
            className="list-group-item"
            style={{ color: colours[item] }}
            key={item}
          >
            {labels[item]}
          </li>
        ))}
      </ul>
    </div>
  );

  useEffect(() => {
    if (filters) {
      setLoading(true);
      getReportData(filters).then((reportData) => {
        setData(reportData.data || []);
        setError(reportData.error);
        setLoading(false);
      });
    }
  }, [filters]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} maxBarSize={80} margin={{ top: 25 }}>
        <XAxis dataKey="time_period" />
        <YAxis />
        <Tooltip animationDuration={100} content={<CustomTooltip />} />
        <Legend
          content={renderLegend}
          layout="vertical"
          align="left"
          verticalAlign="middle"
        />

        {filters &&
          filters.comms.map((comm) =>
            filters.messageStatus.map((status) => (
              <Bar
                key={`${comm}.${status}`}
                dataKey={`${comm}.${status}`}
                name={`${comm} ${status}`}
                stackId={stackBy.value === "messageStatus" ? status : comm}
                fill={
                  colours[stackBy.value === "messageStatus" ? status : comm]
                }
                shape={<BarWithColour />}
              />
            ))
          )}
      </BarChart>
    </ResponsiveContainer>
  );
}
